import { type FetchFanTokenOfferingLobbyByVoteTokenOfferingIdParams, SociosApi } from '@capsicum/common';
import {
	fanTokenOfferingApi,
	useCustomErrorManagementForFanTokenOffering
} from '@capsicum/features/fan-token-offering/shared';
import { useTimerAcceptedAtom, useTimerExpiredDateAtom } from '@capsicum/features/payments/shared';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import type { FanTokenOfferingData, FanTokenOfferingLobbyDataWithPartner } from '../../models';
import type { AxiosError } from 'axios';

/**
 * Shareable query key for reuse else where in the app
 *
 * @param {FetchFanTokenOfferingLobbyByVoteTokenOfferingIdParams} props.params params to send to the endpoint, voteTokenOfferingId and voteTokenOfferingAmount
 * @returns {Array} Array of key
 */
export const useFanTokenOfferingLobbyQueryKey = (params: FetchFanTokenOfferingLobbyByVoteTokenOfferingIdParams) => [
	'paymentFanTokenOfferingLobby',
	params.voteTokenOfferingId,
	params.fiatCurrencyId
];

/**
 * Call Fan Token Offering lobby endpoint which contains data for first screen of user journey.
 *
 * @param {FetchFanTokenOfferingLobbyByVoteTokenOfferingIdParams} props.params params to send to the endpoint, voteTokenOfferingId and voteTokenOfferingAmount
 * @param {(data: any) => any} props.select select that will transform endpoint answer
 * @returns {<any, unknown, any>} return react query
 */
export const useFanTokenOfferingLobbyQuery = (
	params: FetchFanTokenOfferingLobbyByVoteTokenOfferingIdParams,
	select: (data: FanTokenOfferingLobbyDataWithPartner) => FanTokenOfferingData
) => {
	const { timerExpiredAccepted } = useTimerAcceptedAtom();
	const { manageFanTokenOfferingInterceptor } = useCustomErrorManagementForFanTokenOffering();
	const { configExpirationPeriod } = useTimerExpiredDateAtom();

	return useQuery<FanTokenOfferingLobbyDataWithPartner, unknown, FanTokenOfferingData>({
		enabled: !!params.voteTokenOfferingId && !!params.fiatCurrencyId && timerExpiredAccepted,
		queryFn: async () => {
			return (await fanTokenOfferingApi).fetchFanTokenOfferingLobbyByVoteTokenOfferingId(params, {
				customErrorInterceptor: (error: AxiosError<unknown, any> | Error) =>
					manageFanTokenOfferingInterceptor(error as AxiosError<unknown, any>),
				headers: SociosApi.prepareEDTOHeaders([
					{
						at: '/data/voteToken/partnerId',
						details: ['partnerImages'],
						fetch: 'AclService:Partner:byId',
						into: 'partner'
					}
				]),
				// TODO: Check isIOS17 or above
				paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat', format: 'RFC3986' })
			}) as Promise<FanTokenOfferingLobbyDataWithPartner>;
		},
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: useFanTokenOfferingLobbyQueryKey(params),
		/**
		 * For avoiding automatic refetching when navigating between pages.
		 * Relevant for the price timer.
		 */
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		select: select,
		staleTime: configExpirationPeriod
	});
};
